import { createSelector, current } from '@reduxjs/toolkit';
import { filterEnquirySectionsByTag } from '../utilities/application/enquiryHelper';
import {
  CLIENT_DETAILS_TAG,
  application_uw_fields,
} from '../constants/application';
import { PRESALE_CLIENT_DETAILS_TAG } from '../constants/qi';
import { getAge } from '../utilities/date/dateHelper';
import { isEmptyObj } from '../utilities/common';

const getApplicants = (state) => state.underwriting.applicants;

/**
 * Get the UW applicant by the applicantId from the underwriting applicants array
 * called like this useSelector((state) => getUWByApplicantId(state, applicantId));
 */
export const getUWByApplicantId = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.find(
      (applicant) => applicant.enquiry.applicantId === applicantId
    )
);

export const getUWAnswersByApplicantId = createSelector(
  [getUWByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.enquiry.allAnswers : {})
);

export const getUWPreCheckByApplicantId = createSelector(
  [getUWByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.enquiry.preCheck : false)
);

export const getUWEnquiryByApplicantId = createSelector(
  [getUWByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.enquiry : null)
);

export const getUWEnquiryIsOpen = createSelector(
  [getUWByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.enquiry.isOpen : null)
);

export const getUWEnquirySectionsByApplicantId = createSelector(
  [getUWEnquiryByApplicantId, (state, applicantId) => applicantId],
  (enquiry) =>
    enquiry ? enquiry.sections.filter((section) => !!section.name) : []
);

export const getUWSelectedSectionByApplicantId = createSelector(
  [getUWByApplicantId, (state, applicantId) => applicantId],
  (applicant) => (applicant ? applicant.section.selected : null)
);

export const getUWApplicantsLoading = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter((applicant) => applicant.enquiry.loading === true)
      .length > 0
);

export const getClientDetailsEnquirySections = createSelector(
  getUWEnquirySectionsByApplicantId,
  (sections) =>
    sections ? filterEnquirySectionsByTag(sections, CLIENT_DETAILS_TAG) : []
);

export const getEnquirySectionsByTag = createSelector(
  [getUWEnquirySectionsByApplicantId, (state, applicantId, tag) => tag],
  (sections, tag) => (sections ? filterEnquirySectionsByTag(sections, tag) : [])
);

export const getUWEnquiryExclusivesByApplicantId = createSelector(
  getUWEnquiryByApplicantId,
  (enquiry) => (enquiry && enquiry.exclusives ? enquiry.exclusives : [])
);

export const getUWAvailableConditionsByApplicantId = createSelector(
  [getUWEnquiryByApplicantId, (state, applicantId) => applicantId],
  (enquiry) => (enquiry ? enquiry.availableConditions : null)
);

export const getUWAcceptedPreUnderwritingDeclarationByApplicantId =
  createSelector(
    [getUWByApplicantId, (state, applicantId) => applicantId],
    (applicant) =>
      applicant ? applicant.declaration.acceptedPreUnderwriting : false
  );

export const getUWAcceptedPostUnderwritingDeclarationByApplicantId =
  createSelector(
    [getUWByApplicantId, (state, applicantId) => applicantId],
    (applicant) =>
      applicant ? applicant.declaration.acceptedPostUnderwriting : false
  );

export const getValidUWApplicants = createSelector(
  getApplicants,
  (applicants) =>
    applicants.filter((applicant) => applicant.enquiry.applicantId !== null)
);

export const getUWComplete = createSelector(
  getValidUWApplicants,
  (applicants) => {
    try {
      if (applicants[0].enquiry.sections.length === 0) return false;
    } catch {
      return false;
    }
    return (
      applicants.filter((applicant) => applicant.enquiry.isOpen === false)
        .length === applicants.length
    );
  }
);

export const getUWApplicantIdWithStatus = createSelector(
  [getValidUWApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) => {
    if (applicants.length > 1) {
      const applicant = applicants.find(
        (applicant) => applicant.enquiry.applicantId === applicantId
      );
      if (applicant) {
        return {
          applicantId: applicant.enquiry.applicantId,
          isCloseable: applicant.enquiry.isCloseable,
          isOpen: applicant.enquiry.isOpen,
          isSatisfied: applicant.enquiry.isSatisfied,
          loading: applicant.declaration.loading || applicant.enquiry.loading,
        };
      }
    }
  }
);

export const getUWOtherApplicantIdWithStatus = createSelector(
  [getValidUWApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) => {
    if (applicants.length > 1) {
      const otherApplicant = applicants.find(
        (applicant) => applicant.enquiry.applicantId !== applicantId
      );
      if (otherApplicant) {
        return {
          applicantId: otherApplicant.enquiry.applicantId,
          isCloseable: otherApplicant.enquiry.isCloseable,
          isOpen: otherApplicant.enquiry.isOpen,
          isSatisfied: otherApplicant.enquiry.isSatisfied,
          loading:
            otherApplicant.declaration.loading ||
            otherApplicant.enquiry.loading,
        };
      }
    }
  }
);

/**
 * Get the applicant index from the application applicants array
 */
export const getApplicantIndex = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants, applicantId) =>
    applicants.findIndex(
      (applicant) => applicant.enquiry.applicantId === applicantId
    )
);

export const getInvalidClientDetailsGender = createSelector(
  getUWAnswersByApplicantId,
  (allAnswers) => {
    try {
      if (
        allAnswers[application_uw_fields.gender] &&
        allAnswers[application_uw_fields.gender].length > 0
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
);

export const getInvalidClientDetailsDateOfBirth = createSelector(
  getUWAnswersByApplicantId,
  (allAnswers) => {
    try {
      if (
        allAnswers[application_uw_fields.dateOfBirth] &&
        allAnswers[application_uw_fields.dateOfBirth].length > 0 &&
        allAnswers[application_uw_fields.dateOfBirth][0].length === 10 &&
        getAge(allAnswers[application_uw_fields.dateOfBirth][0]) < 100
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
);

export const getInvalidClientDetailsSmoker = createSelector(
  getUWAnswersByApplicantId,
  (allAnswers) => {
    try {
      if (
        allAnswers[application_uw_fields.smoking] &&
        allAnswers[application_uw_fields.smoking].length > 0
      ) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }
);

/**
 * Check if there any questions that are from an enquiry line with a
 * CLIENT_DETAILS tag that have any validation errors
 */
export const getInvalidClientDetailsBuild = createSelector(
  getClientDetailsEnquirySections,
  (sections) =>
    sections.filter(
      (section) =>
        section.enquiryLines.filter(
          (enquiryLine) =>
            enquiryLine.tags.indexOf('CLIENT_DETAILS') !== -1 &&
            enquiryLine.questions.filter(
              (question) => !isEmptyObj(question.validationErrors)
            ).length > 0
        ).length > 0
    ).length > 0
);

export const getInvalidClientDetailsEnquirySections = createSelector(
  getClientDetailsEnquirySections,
  (sections) =>
    sections.filter(
      (section) =>
        section.enquiryLines.filter(
          (enquiryLine) =>
            !enquiryLine.isSatisfied &&
            enquiryLine.tags.indexOf('OPTIONAL') === -1
        ).length > 0
    ).length === 0
);

export const getUnderwritingTabs = createSelector(
  [getApplicants, (state, applicantId) => applicantId],
  (applicants) =>
    applicants.map((applicant) => ({
      applicantId: applicant.enquiry.applicantId,
      sections: applicant.enquiry.sections,
      isClosable: applicant.enquiry.isCloseable,
      isOpen: applicant.enquiry.isOpen,
    }))
);

export const getProductQuestion = createSelector(
  [getUWEnquirySectionsByApplicantId, (state, applicantId) => applicantId],
  (sections) => {
    if (sections) {
      let productQuestion;
      sections.some((section) => {
        section.enquiryLines.some((enquiryLine) => {
          productQuestion = enquiryLine.questions.find(
            (question) => question.name === 'PRODUCT'
          );
          return true;
        });
        if (productQuestion) return true;
      });
      if (productQuestion) {
        return {
          ...productQuestion,
          definition: {
            ...productQuestion.definition,
            text: 'Covers required for QI underwriting',
          },
        };
      }
      return null;
    }
    return null;
  }
);

export const getApplicantIndexFromApplicantId = (state, applicantId) => {
  let index = getApplicantIndex({ underwriting: current(state) }, applicantId);
  return index;
};

export const getApplicantAnswersFromApplicationId = (state, applicantId) => {
  let index = getApplicantIndex(
    { underwriting: state.underwriting },
    applicantId
  );
  return state.underwriting.applicants[index].enquiry.allAnswers;
};

export const getApplicantIndexFromMeta = (state, action) => {
  const {
    meta: {
      arg: { applicantId },
    },
  } = action;
  let index = getApplicantIndex({ underwriting: current(state) }, applicantId);
  return index;
};

export const getApplicantIndexFromPayloadData = (state, action) => {
  const { applicantId } = action.payload.data;
  let index = getApplicantIndex({ underwriting: current(state) }, applicantId);
  return index;
};

export const getAnswersFromMeta = (action, allAnswers) => {
  const {
    meta: {
      arg: { data },
    },
  } = action;
  let answers = {};
  try {
    Object.keys(data).forEach((key, index) => {
      if (allAnswers[key]) {
        answers[Object.keys(data)[index]] = [Object.values(data)[index]];
      }
    });
    return { ...allAnswers, ...answers };
  } catch {
    return allAnswers;
  }
};

export const isUWApplicantsSatisfied = (applicants) =>
  applicants.filter((applicant) => {
    const sections = filterEnquirySectionsByTag(
      applicant.enquiry.sections,
      CLIENT_DETAILS_TAG
    );
    return (
      sections.filter(
        (section) =>
          section.enquiryLines.filter(
            (enquiryLine) => enquiryLine.isSatisfied === false
          ).length > 0
      ).length > 0
    );
  }).length === 0;

export const isQIUWApplicantsSatisfied = (applicants) =>
  applicants.filter((applicant) => {
    const sections = filterEnquirySectionsByTag(
      applicant.enquiry.sections,
      PRESALE_CLIENT_DETAILS_TAG
    );
    return (
      sections.filter(
        (section) =>
          section.enquiryLines.filter(
            (enquiryLine) => enquiryLine.isSatisfied === false
          ).length > 0
      ).length > 0
    );
  }).length === 0;
